/**
 * CollaboratorCursors — cursor sharing for the open workspace, kept out of App.
 *
 * It owns the cursor hook, listens to the pointer itself and draws the other users' cursors,
 * so a cursor update (up to 16 a second for each collaborator) renders this component and
 * nothing else. This user's own cursor is sent only while someone else is there to see it.
 */

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCursorSync } from '../hooks/useCursorSync';
import { CursorOverlay } from './CursorOverlay';
import type { ViewportTransform } from '../types';

/** What this needs of the canvas; components/InfiniteCanvas.tsx gives it through its ref. */
export interface CursorCanvas {
  containerRef: React.RefObject<HTMLDivElement>;
  /** The view right now, also in the middle of a pan or zoom. */
  getLiveView: () => ViewportTransform;
}

interface CollaboratorCursorsProps {
  projectId: string | null;
  /** Cursors are shared in this workspace (a cloud workspace, signed in). */
  enabled: boolean;
  /** Someone else is in the workspace. */
  othersPresent: boolean;
  /** The canvas is covered by the Edit page: this user's cursor is not sent from there. */
  paused: boolean;
  canvasRef: React.RefObject<CursorCanvas | null>;
  /** The view as React has it (set when a pan or zoom ends). */
  transform: ViewportTransform;
}

/** A point of the window in canvas (world) coordinates. */
export const toCanvasPoint = (
  clientX: number,
  clientY: number,
  view: ViewportTransform,
  offset: { x: number; y: number },
): { x: number; y: number } => ({
  x: (clientX - offset.x - view.x) / view.scale,
  y: (clientY - offset.y - view.y) / view.scale,
});

const NO_OFFSET = { x: 0, y: 0 };

const CollaboratorCursorsComponent: React.FC<CollaboratorCursorsProps> = ({ projectId, enabled, othersPresent, paused, canvasRef, transform }) => {
  const { remoteCursors, broadcastCursor } = useCursorSync(projectId, enabled, othersPresent);
  // Where the canvas container starts in the window: measured when it can change, not on every pointer move
  const [offset, setOffset] = useState(NO_OFFSET);
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const transformRef = useRef(transform);
  transformRef.current = transform;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  const sharing = enabled && (othersPresent || remoteCursors.length > 0);
  useLayoutEffect(() => {
    if (!sharing) return;
    const container = canvasRef.current?.containerRef.current ?? null;
    const measure = () => {
      const rect = container?.getBoundingClientRect();
      const next = rect ? { x: rect.left, y: rect.top } : NO_OFFSET;
      setOffset(previous => (previous.x === next.x && previous.y === next.y ? previous : next));
    };
    measure();
    // The container moves only when it is resized (the window, the view-only banner above it)
    const observer = container && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && container) observer.observe(container);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
    // (also when another workspace opens, and when the canvas comes back from under the Edit page)
  }, [sharing, canvasRef, projectId, paused]);

  // The live view: React's own is the one from before the gesture for as long as a pan or zoom lasts
  const getView = useCallback(
    (): ViewportTransform => canvasRef.current?.getLiveView?.() ?? transformRef.current,
    [canvasRef],
  );

  useEffect(() => {
    if (!enabled || !othersPresent) return;
    const onPointerMove = (event: PointerEvent) => {
      if (pausedRef.current) return;
      const point = toCanvasPoint(event.clientX, event.clientY, getView(), offsetRef.current);
      broadcastCursor(point.x, point.y);
    };
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, [enabled, othersPresent, broadcastCursor, getView]);

  if (!enabled || remoteCursors.length === 0) return null;
  return <CursorOverlay cursors={remoteCursors} transform={transform} getView={getView} containerOffset={offset} />;
};

/** Memoized: an App render that changes none of its props does not reach it. */
export const CollaboratorCursors = React.memo(CollaboratorCursorsComponent);
